import { useState, useEffect, useRef } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import {
  useSangamSession,
  useSangamParticipants,
  useSangamTurns,
  useRegisterParticipant,
  useSangamAnnouncements,
  useUpcomingSangamSessions,
  useAdminSangamControl,
  usePastSangamSessions,
  useApproveParticipant,
  useRemoveParticipant,
  useRequeueParticipant,
  usePurgeParticipant,
} from '@/hooks/useFirestore'
import { useAuthStore } from '@/store/authStore'
import { 
  Users, 
  MessageSquare, 
  Mic2, 
  MicOff,
  ShieldCheck, 
  Loader2, 
  Play, 
  Pause,
  Clock,
  ExternalLink,
  Megaphone,
  Calendar,
  Zap,
  Square,
  History, 
  Hand, 
  ArrowRight,
  Volume2,
  VolumeX,
  UserMinus,
  AlertTriangle,
  SkipForward,
  Target,
  Radio,
  RotateCcw,
  Trash2,
  UserCheck,
  UserX,
  Headphones,
  Copy,
} from 'lucide-react'
import type { AgentUIState, AdminCommand } from '@/types'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'

export default function SangamPage() {
  const [searchParams] = useSearchParams()
  const initialSessionId = searchParams.get('id')

  const { isAdmin } = useAuthStore()
  const { data: session, isLoading: sessionLoading, sessionId } = useSangamSession(initialSessionId)
  const { data: participants, isLoading: partsLoading } = useSangamParticipants(sessionId || undefined)
  const { data: turns, isLoading: turnsLoading } = useSangamTurns(sessionId || undefined)
  
  const activeParticipants = participants?.filter(p => p.status !== 'REMOVED') || []
  const approvedCount = participants?.filter(p => p.status === 'APPROVED').length || 0
  const pendingCount = participants?.filter(p => p.status === 'PENDING').length || 0
  const removedParticipants = participants?.filter(p => p.status === 'REMOVED') || []
  // Use active (non-removed) participants list length when available
  const participantsCount = session ? (activeParticipants.length || session.participants_count || 0) : 0
  const { data: announcements } = useSangamAnnouncements()
  const { data: upcomingSessions } = useUpcomingSangamSessions()
  const { data: pastSessions } = usePastSangamSessions()
  const registerMutation = useRegisterParticipant()
  const adminControlMutation = useAdminSangamControl()
  const approvePartMutation = useApproveParticipant()
  const removePartMutation = useRemoveParticipant()
  const requeuePartMutation = useRequeueParticipant()
  const purgePartMutation = usePurgeParticipant()

  const [registerName, setRegisterName] = useState('')
  const [registerBio, setRegisterBio] = useState('')
  const [refocusText, setRefocusText] = useState('')
  const [forceAgentName, setForceAgentName] = useState('')
  const [isStartingUp, setIsStartingUp] = useState(false)

  // Helper to dispatch admin commands
  const dispatchAdminCommand = (command: AdminCommand, targetAgent?: string, payload?: string) => {
    if (!sessionId) {
      console.error('[Admin] Cannot dispatch command — no active session ID')
      return
    }
    console.log(`[Admin] Dispatching ${command} to session ${sessionId}`)
    adminControlMutation.mutate({
      sessionId,
      adminCommand: command,
      targetAgent,
      payload,
    })
  }
  const [isAudioEnabled, setIsAudioEnabled] = useState(session?.audio_enabled === true)  // Default text-only; enable in UI for audio
  const [currentAudioUrl, setCurrentAudioUrl] = useState<string | null>(null)
  const [currentPlayingAgent, setCurrentPlayingAgent] = useState<string | null>(null)

  // Voice preview
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)
  const [previewPlaying, setPreviewPlaying] = useState<string | null>(null) // agent name currently previewing
  const audioQueueRef = useRef<Array<{ turnId: string; audio_url: string; agent_name: string }>>([])
  const playedTurnIdsRef = useRef<Set<string>>(new Set())
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const isPlayingRef = useRef(false)
  const [currentPlayingTurnId, setCurrentPlayingTurnId] = useState<string | null>(null)
  const [revealedTurnIds, setRevealedTurnIds] = useState<Set<string>>(new Set())

  // Alias session early so it's available before the loading guard
  const activeSession = session

  const isRecordedSession = activeSession?.status === 'RECORDED' || activeSession?.status === 'COMPLETED'
  const isLiveOrPaused = activeSession?.status === 'LIVE' || activeSession?.status === 'PAUSED'
  const turnsWithAudio = turns?.filter(t => t.audio_url) || []
  const showAgentRegistration = false // Set to true to show "Agent Registration" section
  const showOtherSessions = false // Set to true to show "Other Sessions" sidebar card
  const [copiedJustNow, setCopiedJustNow] = useState(false)
  const [isApprovingAll, setIsApprovingAll] = useState(false)

  const approveAllPending = async () => {
    const pending = participants?.filter(p => p.status === 'PENDING') || []
    if (pending.length === 0 || !activeSession?.id) return
    setIsApprovingAll(true)
    try {
      for (const p of pending) {
        await approvePartMutation.mutateAsync({ sessionId: activeSession.id, participantId: p.id })
      }
    } finally {
      setIsApprovingAll(false)
    }
  }

  const copyTranscript = async () => {
    if (!turns?.length) return
    const text = turns
      .map((t) => `${t.agent_name}: ${(t.transcript || '').trim()}`)
      .join('\n\n')
    try {
      await navigator.clipboard.writeText(text)
      setCopiedJustNow(true)
      setTimeout(() => setCopiedJustNow(false), 2000)
    } catch {
      setCopiedJustNow(false)
    }
  }

  // Clear "starting up" state once session transitions away from WAITING
  useEffect(() => {
    if (activeSession && activeSession.status !== 'WAITING') {
      setIsStartingUp(false)
    }
  }, [activeSession?.status])

  // Sync audio toggle from Firestore when session data loads
  useEffect(() => {
    if (activeSession) {
      setIsAudioEnabled(activeSession.audio_enabled === true)
    }
  }, [activeSession?.audio_enabled])

  // Build queue of turns with audio that haven't been played yet (in transcript order)
  useEffect(() => {
    if (!isAudioEnabled || !turns || turns.length === 0) return

    const toQueue = turns.filter(
      (t) => t.audio_url && !playedTurnIdsRef.current.has(t.id)
    )
    if (toQueue.length === 0) return

    toQueue.forEach((t) => {
      playedTurnIdsRef.current.add(t.id)
      audioQueueRef.current.push({ turnId: t.id, audio_url: t.audio_url!, agent_name: t.agent_name })
    })

    if (!isPlayingRef.current && audioQueueRef.current.length > 0) {
      const next = audioQueueRef.current.shift()!
      setCurrentAudioUrl(next.audio_url)
      setCurrentPlayingAgent(next.agent_name)
      setCurrentPlayingTurnId(next.turnId)
      isPlayingRef.current = true
    }
  }, [turns, isAudioEnabled])

  const playNextInQueue = () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false
      setCurrentAudioUrl(null)
      setCurrentPlayingAgent(null)
      setCurrentPlayingTurnId(null)
      return
    }
    const next = audioQueueRef.current.shift()!
    setCurrentAudioUrl(next.audio_url)
    setCurrentPlayingAgent(next.agent_name)
    setCurrentPlayingTurnId(next.turnId)
  }

  const handleAudioEnded = () => {
    // Reveal the transcript for the turn that just finished playing
    if (currentPlayingTurnId) {
      setRevealedTurnIds(prev => new Set([...prev, currentPlayingTurnId]))
    }
    setCurrentAudioUrl(null)
    setCurrentPlayingAgent(null)
    setCurrentPlayingTurnId(null)
    playNextInQueue()
  }

  // Start playback when we set a new URL
  useEffect(() => {
    if (!currentAudioUrl || !audioRef.current) return
    audioRef.current.play().catch((err) => {
      console.warn('Playback failed:', err)
      setIsAudioEnabled(false)
    })
  }, [currentAudioUrl])

  // Play Full Podcast (for recorded sessions) – queue ALL turns with audio
  const playFullPodcast = () => {
    if (!turns || turns.length === 0) return
    setIsAudioEnabled(true)
    playedTurnIdsRef.current.clear()
    audioQueueRef.current = []
    isPlayingRef.current = false
    setRevealedTurnIds(new Set())

    const allWithAudio = turns.filter(t => t.audio_url)
    if (allWithAudio.length === 0) return

    allWithAudio.forEach(t => {
      playedTurnIdsRef.current.add(t.id)
      audioQueueRef.current.push({ turnId: t.id, audio_url: t.audio_url!, agent_name: t.agent_name })
    })

    const first = audioQueueRef.current.shift()!
    setCurrentAudioUrl(first.audio_url)
    setCurrentPlayingAgent(first.agent_name)
    setCurrentPlayingTurnId(first.turnId)
    isPlayingRef.current = true
  }

  // Stop audio
  const stopAudio = () => {
    setIsAudioEnabled(false)
    setCurrentAudioUrl(null)
    setCurrentPlayingAgent(null)
    audioQueueRef.current = []
    isPlayingRef.current = false
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
  }

  // Voice preview: play a short intro clip for an agent (cache-bust so updates are heard)
  const PREVIEW_VERSION = 2 // Bump when preview audio is re-generated
  const playPreview = (agentName: string) => {
    const key = agentName.toLowerCase().replace(/\s+/g, '')
    // Map known names to preview files
    const previewMap: Record<string, string> = {
      'nexus': 'nexus', 'nexusagent': 'nexus',
      'nova': 'nova', 'iron': 'iron', 'root': 'root',
      'logic': 'logic', 'zenith': 'zenith', 'spark': 'spark',
    }
    const file = previewMap[key]
    if (!file) return

    const url = `/audio/previews/${file}_preview.mp3?v=${PREVIEW_VERSION}`

    // If already playing this agent, stop it
    if (previewPlaying === agentName && previewAudioRef.current) {
      previewAudioRef.current.pause()
      previewAudioRef.current.currentTime = 0
      setPreviewPlaying(null)
      return
    }

    // Stop any existing preview
    if (previewAudioRef.current) {
      previewAudioRef.current.pause()
      previewAudioRef.current.currentTime = 0
    }

    const audio = new Audio(url)
    previewAudioRef.current = audio
    setPreviewPlaying(agentName)
    audio.play().catch(() => setPreviewPlaying(null))
    audio.onended = () => setPreviewPlaying(null)
    audio.onerror = () => setPreviewPlaying(null)
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!registerName) return
    await registerMutation.mutateAsync({
      name: registerName,
      bio: registerBio,
    })
    setRegisterName('')
    setRegisterBio('')
  }

  // Agent UI state indicator (§7)
  const agentStateConfig: Record<AgentUIState, { label: string; color: string; icon: React.ReactNode }> = {
    SPEAKING: { label: 'Speaking', color: 'bg-green-500', icon: <Mic2 className="w-2.5 h-2.5" /> },
    LISTENING: { label: 'Listening', color: 'bg-blue-400', icon: <Radio className="w-2.5 h-2.5" /> },
    CLAIMING_MIC: { label: 'Claiming Mic', color: 'bg-yellow-500', icon: <Hand className="w-2.5 h-2.5" /> },
    INTERRUPTED_MOD: { label: 'Interrupted', color: 'bg-orange-500', icon: <AlertTriangle className="w-2.5 h-2.5" /> },
    INTERRUPTED_ADMIN: { label: 'Admin Override', color: 'bg-red-500', icon: <AlertTriangle className="w-2.5 h-2.5" /> },
    MUTED: { label: 'Muted', color: 'bg-gray-500', icon: <MicOff className="w-2.5 h-2.5" /> },
  }

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    )
  }

  // Handle case where no session is found, but we still want to show the page with past/upcoming lists
  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Announcements Bar */}
      {announcements && announcements.length > 0 && (
        <div className="bg-primary-600 text-white px-6 py-3 rounded-xl shadow-lg flex items-center space-x-4 animate-in fade-in slide-in-from-top duration-500">
          <Megaphone className="w-5 h-5 flex-shrink-0 animate-bounce" />
          <div className="flex-1 overflow-hidden">
            <div className="flex animate-marquee whitespace-nowrap space-x-8">
              {announcements.map((ann) => (
                <span key={ann.id} className="text-sm font-medium">
                  <span className="font-bold uppercase mr-2">[{ann.title}]</span>
                  {ann.content}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hero Header for Scheduled / Past Sessions */}
      {(!activeSession || activeSession.status === 'WAITING' || activeSession.status === 'RECORDED') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Upcoming Card — only show WAITING sessions */}
          {(() => {
            const waitingSessions = upcomingSessions?.filter(s => s.status === 'WAITING') || []
            const nextSession = waitingSessions[0]
            return (
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <Calendar className="w-8 h-8 opacity-50" />
                  <span className="text-xs font-bold uppercase tracking-widest bg-white/20 px-2 py-1 rounded">Next Session</span>
                </div>
                {nextSession ? (
                  <div className="space-y-3">
                    <h2 className="text-xl font-bold leading-tight line-clamp-2">{nextSession.topic}</h2>
                    <div className="flex items-center text-sm text-primary-100">
                      <Clock className="w-4 h-4 mr-2" />
                      <span>{nextSession.scheduled_at ? format(nextSession.scheduled_at, 'EEEE, MMM d @ h:mm a') : 'TBD'}</span>
                    </div>
                    <div className="pt-2">
                      <Link 
                        to={"/?id=" + nextSession.id}
                        className="inline-flex items-center text-sm font-bold bg-white text-primary-700 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors"
                      >
                        View Details
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Link>
                    </div>
                  </div>
                ) : (
                  <p className="text-primary-100 italic">No upcoming sessions scheduled yet.</p>
                )}
              </div>
            )
          })()}

          {/* Stats Card */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-800 shadow-sm grid grid-cols-2 gap-4">
            <Link to="/recordings" className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors group">
              <History className="w-6 h-6 mx-auto mb-2 text-primary-500 group-hover:scale-110 transition-transform" />
              <div className="text-2xl font-bold dark:text-white">{pastSessions?.length || 0}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-tighter">View Archive</div>
            </Link>
            <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <Users className="w-6 h-6 mx-auto mb-2 text-secondary-500" />
              <div className="text-2xl font-bold dark:text-white">6</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-tighter">Council Agents</div>
            </div>
          </div>
        </div>
      )}

      {/* Active Session Stage */}
      {activeSession ? (
        <>
<div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-primary-900 text-white p-8 shadow-xl">
        <div className="absolute top-0 right-0 p-6 opacity-10">
          <Users className="w-32 h-32" />
        </div>
        
        <div className="relative z-10 space-y-4">
          <div className="flex items-center space-x-3 flex-wrap gap-y-2">
            <span className={cn(
              "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider flex items-center space-x-2",
              activeSession.status === 'LIVE' ? "bg-red-500/20 text-red-400 border border-red-500/30" : 
              activeSession.status === 'PAUSED' ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30" :
              activeSession.status === 'RECORDED' ? "bg-green-500/20 text-green-400 border border-green-500/30" :
              "bg-primary-500/20 text-primary-300 border border-primary-500/30"
            )}>
              {activeSession.status === 'LIVE' && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse mr-1" />}
              {activeSession.status === 'PAUSED' && <Pause className="w-3 h-3 mr-1" />}
              {activeSession.status}
            </span>
            {activeSession.phase && ['LIVE', 'PAUSED'].includes(activeSession.status) && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-white/10 text-gray-300 border border-white/10">
                Phase: {activeSession.phase?.replace('_', ' ')}
              </span>
            )}
            {activeSession.status === 'WAITING' && (
              <span className="text-sm text-gray-400 flex items-center">
                <Users className="w-4 h-4 mr-1" />
                {participantsCount}/5 Agents Registered
              </span>
            )}
            {/* Admin Override indicator (§7 – subtle) */}
            {activeSession.admin_override && (
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-400 border border-red-500/30 animate-pulse">
                Admin Override
              </span>
            )}
          </div>

          <h1 className="text-3xl md:text-4xl font-display font-bold max-w-3xl leading-tight">
            {activeSession.topic}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-300">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary-400" />
              <span>Moderated by Nexus Agent</span>
              <button
                onClick={(e) => { e.stopPropagation(); playPreview('Nexus') }}
                className={cn(
                  "flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-all",
                  previewPlaying === 'Nexus'
                    ? "bg-green-500 text-white animate-pulse"
                    : "bg-white/20 text-white/70 hover:bg-white/30 hover:text-white"
                )}
                title={previewPlaying === 'Nexus' ? "Stop preview" : "Hear Nexus Agent's voice"}
              >
                {previewPlaying === 'Nexus' ? (
                  <Square className="w-2 h-2" />
                ) : (
                  <Volume2 className="w-2.5 h-2.5" />
                )}
              </button>
            </div>
            {activeSession.started_at && (
              <div className="flex items-center">
                <Play className="w-4 h-4 mr-2 text-green-400" />
                <span>Started {format(activeSession.started_at, 'MMM d, h:mm a')}</span>
              </div>
            )}
            <button
              onClick={() => {
                const newVal = !isAudioEnabled
                setIsAudioEnabled(newVal)
                // Write to Firestore so backend knows whether to generate voice
                adminControlMutation.mutate({
                  sessionId: sessionId || undefined,
                  audioEnabled: newVal,
                })
              }}
              className={cn(
                "flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all border",
                isAudioEnabled 
                  ? "bg-green-500/20 text-green-400 border-green-500/30" 
                  : "bg-red-500/20 text-red-300 border-red-500/30 hover:bg-red-500/40"
              )}
            >
              {isAudioEnabled ? (
                <>
                  <Volume2 className="w-3.5 h-3.5 animate-pulse" />
                  <span>Audio On</span>
                </>
              ) : (
                <>
                  <VolumeX className="w-3.5 h-3.5" />
                  <span>Audio Off</span>
                </>
              )}
            </button>
          </div>

          <audio
            ref={audioRef}
            src={currentAudioUrl || undefined}
            className="hidden"
            onEnded={handleAudioEnded}
          />

          {/* Admin Control Plane (§6 – highest priority) */}
          {isAdmin() && (
            <div className="mt-6 pt-6 border-t border-white/10 space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-primary-300">Admin Control Plane</span>

              {/* WAITING state controls */}
              {activeSession.status === 'WAITING' && participantsCount >= 1 && (
                <div className="flex flex-col space-y-1">
                  {isStartingUp ? (
                    <div className="flex items-center space-x-3 px-4 py-3 bg-yellow-600/20 border border-yellow-500/30 text-yellow-300 rounded-lg">
                      <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                      <div>
                        <span className="text-sm font-bold">Starting up...</span>
                        <span className="block text-[10px] text-yellow-400/70 mt-0.5">Moderator is preparing the session. This may take a moment.</span>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setIsStartingUp(true)
                        adminControlMutation.mutate({ moderatorApproved: true })
                      }}
                      disabled={adminControlMutation.isPending || approvedCount < 2}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg text-sm font-bold transition-all shadow-lg hover:shadow-green-500/20"
                    >
                      <Zap className="w-4 h-4" />
                      <span>Go Ahead (Start Live)</span>
                    </button>
                  )}
                  {!isStartingUp && approvedCount < 2 && (
                    <span className="text-[10px] text-accent-400 italic">Approve at least 2 agents to enable start. ({approvedCount}/2 ready)</span>
                  )}
                  {!isStartingUp && (
                    <span className="text-[10px] text-gray-400 block mt-1">
                      Moderator must be running: <code className="bg-white/10 px-1 rounded">python sangam_moderator.py</code>
                    </span>
                  )}
                </div>
              )}
              {activeSession.status === 'WAITING' && participantsCount === 0 && (
                <span className="text-xs italic text-gray-400">
                  Run <code className="bg-white/10 px-1 rounded">strategic-council/council_launcher.py</code> to start agents.
                </span>
              )}

              {/* LIVE / PAUSED state controls */}
              {['LIVE', 'PAUSED'].includes(activeSession.status) && (
                <div className="space-y-3">
                  {/* Row 1: Core controls */}
                  <div className="flex flex-wrap gap-2">
                    {activeSession.status === 'LIVE' ? (
                      <button onClick={() => dispatchAdminCommand('PAUSE')} disabled={adminControlMutation.isPending}
                        className="flex items-center space-x-1.5 px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-xs font-bold transition-all">
                        <Pause className="w-3.5 h-3.5" /><span>Pause</span>
                      </button>
                    ) : (
                      <button onClick={() => dispatchAdminCommand('RESUME')} disabled={adminControlMutation.isPending}
                        className="flex items-center space-x-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold transition-all">
                        <Play className="w-3.5 h-3.5" /><span>Resume</span>
                      </button>
                    )}
                    <button onClick={() => dispatchAdminCommand('CUT_TO_MODERATOR')} disabled={adminControlMutation.isPending}
                      className="flex items-center space-x-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-bold transition-all">
                      <ShieldCheck className="w-3.5 h-3.5" /><span>Cut to Moderator</span>
                    </button>
                    <button onClick={() => dispatchAdminCommand('SKIP_CURRENT_SPEAKER')} disabled={adminControlMutation.isPending}
                      className="flex items-center space-x-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs font-bold transition-all">
                      <SkipForward className="w-3.5 h-3.5" /><span>Skip Speaker</span>
                    </button>
                    <button onClick={() => { if (confirm('End this episode now?')) dispatchAdminCommand('END_EPISODE_NOW') }} disabled={adminControlMutation.isPending}
                      className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-all">
                      <Square className="w-3.5 h-3.5" /><span>End Episode</span>
                    </button>
                  </div>

                  {/* Row 2: Force speaker */}
                  <div className="flex items-center gap-2">
                    <select value={forceAgentName} onChange={e => setForceAgentName(e.target.value)}
                      className="bg-white/10 border border-white/20 text-white text-xs rounded-lg px-2 py-1.5 flex-1">
                      <option value="" className="text-gray-900">Select agent...</option>
                      {participants?.filter(p => p.status === 'APPROVED').map(p => (
                        <option key={p.id} value={p.name} className="text-gray-900">{p.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => { if (forceAgentName) { dispatchAdminCommand('FORCE_NEXT_SPEAKER', forceAgentName); setForceAgentName('') } }}
                      disabled={!forceAgentName || adminControlMutation.isPending}
                      className="flex items-center space-x-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-600 text-white rounded-lg text-xs font-bold transition-all">
                      <Mic2 className="w-3.5 h-3.5" /><span>Force Speaker</span>
                    </button>
                    <button
                      onClick={() => { if (forceAgentName) { dispatchAdminCommand('MUTE_AGENT', forceAgentName) } }}
                      disabled={!forceAgentName || adminControlMutation.isPending}
                      className="flex items-center space-x-1.5 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-700 text-white rounded-lg text-xs font-bold transition-all">
                      <MicOff className="w-3.5 h-3.5" /><span>Mute</span>
                    </button>
                    <button
                      onClick={() => { if (forceAgentName) { dispatchAdminCommand('UNMUTE_AGENT', forceAgentName) } }}
                      disabled={!forceAgentName || adminControlMutation.isPending}
                      className="flex items-center space-x-1.5 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-700 text-white rounded-lg text-xs font-bold transition-all">
                      <Mic2 className="w-3.5 h-3.5" /><span>Unmute</span>
                    </button>
                    <button
                      onClick={() => { if (forceAgentName && confirm(`Remove ${forceAgentName}?`)) { dispatchAdminCommand('REMOVE_AGENT', forceAgentName); setForceAgentName('') } }}
                      disabled={!forceAgentName || adminControlMutation.isPending}
                      className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white rounded-lg text-xs font-bold transition-all">
                      <UserMinus className="w-3.5 h-3.5" /><span>Remove</span>
                    </button>
                  </div>

                  {/* Row 3: Refocus */}
                  <div className="flex items-center gap-2">
                    <input
                      type="text" value={refocusText} onChange={e => setRefocusText(e.target.value)}
                      placeholder="Refocus topic or question..."
                      className="bg-white/10 border border-white/20 text-white placeholder-gray-400 text-xs rounded-lg px-3 py-1.5 flex-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                    <button
                      onClick={() => { if (refocusText) { dispatchAdminCommand('REFOCUS', undefined, refocusText); setRefocusText('') } }}
                      disabled={!refocusText || adminControlMutation.isPending}
                      className="flex items-center space-x-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-600 text-white rounded-lg text-xs font-bold transition-all">
                      <Target className="w-3.5 h-3.5" /><span>Refocus</span>
                    </button>
                  </div>

                  {/* Muted agents indicator */}
                  {activeSession.muted_agents && activeSession.muted_agents.length > 0 && (
                    <div className="text-[10px] text-gray-400">
                      Muted: {activeSession.muted_agents.join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ============================================================ */}
      {/* ARCHIVE VIEW — for recorded sessions                         */}
      {/* ============================================================ */}
      {isRecordedSession && (
        <div className="space-y-6">
          {/* Single Podcast Player */}
          {activeSession.podcast_audio_url ? (
            <div className="bg-gradient-to-r from-primary-900/80 to-gray-900 rounded-xl border border-primary-700/30 p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <Headphones className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Full Podcast</h3>
                    <p className="text-gray-400 text-sm">
                      {activeSession.podcast_duration_seconds
                        ? `${Math.floor(activeSession.podcast_duration_seconds / 60)}m ${activeSession.podcast_duration_seconds % 60}s`
                        : `${turnsWithAudio.length} segments`}
                      {' · '}{participants?.filter(p => p.status === 'APPROVED').length || 0} agents
                    </p>
                  </div>
                </div>
              </div>
              <audio
                controls
                src={activeSession.podcast_audio_url}
                className="w-full h-12 rounded-lg"
                style={{ filter: 'invert(1) hue-rotate(180deg) brightness(0.8)' }}
              />
            </div>
          ) : turnsWithAudio.length > 0 ? (
            <div className="bg-gradient-to-r from-primary-900/80 to-gray-900 rounded-xl border border-primary-700/30 p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <Headphones className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Listen to Podcast</h3>
                    <p className="text-gray-400 text-sm">{turnsWithAudio.length} audio segments · Play sequentially</p>
                  </div>
                </div>
                <button
                  onClick={playFullPodcast}
                  className="flex items-center space-x-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-bold transition-all shadow-lg hover:shadow-primary-500/20"
                >
                  <Play className="w-5 h-5" />
                  <span>Play All</span>
                </button>
              </div>
              {isAudioEnabled && currentPlayingAgent && (
                <div className="mt-4 flex items-center space-x-3 text-primary-300">
                  <Volume2 className="w-4 h-4 animate-pulse" />
                  <span className="text-sm font-medium">Now playing: {currentPlayingAgent}</span>
                  <div className="flex items-center space-x-0.5">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className="w-0.5 bg-primary-400 rounded-full animate-pulse" style={{ height: `${6 + Math.random() * 8}px`, animationDelay: `${i * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Full Transcript */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
              <h3 className="font-bold flex items-center space-x-2 dark:text-white">
                <MessageSquare className="w-5 h-5 text-primary-500" />
                <span>Full Transcript</span>
              </h3>
              {isAdmin() && turns && turns.length > 0 && (
                <button
                  type="button"
                  onClick={copyTranscript}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {copiedJustNow ? 'Copied!' : 'Copy transcript'}
                </button>
              )}
            </div>
            <div className="p-6 space-y-6 max-h-[800px] overflow-y-auto">
              {turns?.map((turn) => (
                <div key={turn.id} className={cn("flex flex-col space-y-1", turn.is_moderator ? "items-center" : "items-start")}>
                  <div className={cn(
                    "max-w-[90%] rounded-2xl p-4 shadow-sm",
                    turn.is_moderator
                      ? "bg-primary-50 dark:bg-primary-900/10 border border-primary-100 dark:border-primary-900/20 italic text-center"
                      : "bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50"
                  )}>
                    {!turn.is_moderator && (
                      <span className="block text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wider mb-1">
                        {turn.agent_name}
                      </span>
                    )}
                    <p className="text-gray-800 dark:text-gray-200 leading-relaxed">{turn.transcript}</p>
                    <span className="block text-[10px] text-gray-400 text-right mt-1">
                      {format(turn.timestamp, 'h:mm:ss a')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Participants list for archive */}
          {participants && participants.length > 0 && (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
              <h3 className="font-bold flex items-center space-x-2 dark:text-white mb-4">
                <Users className="w-5 h-5 text-primary-500" />
                <span>Participants</span>
              </h3>
              <div className="flex flex-wrap gap-3">
                {participants.filter(p => p.status === 'APPROVED').map(p => (
                  <div key={p.id} className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold text-xs">
                      {p.name.charAt(0)}
                    </div>
                    <div>
                      <span className="text-sm font-bold dark:text-white">{p.name}</span>
                      {p.personality && <span className="block text-[10px] text-gray-400 line-clamp-1">{p.personality}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* LIVE VIEW — for active/waiting sessions                      */}
      {/* ============================================================ */}
      {!isRecordedSession && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Stage - Transcript */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden flex flex-col h-[600px]">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
              <h3 className="font-bold flex items-center space-x-2 dark:text-white">
                <MessageSquare className="w-5 h-5 text-primary-500" />
                <span>Live Transcript</span>
              </h3>
              <div className="flex items-center gap-2">
                {isAdmin() && turns && turns.length > 0 && (
                  <button
                    type="button"
                    onClick={copyTranscript}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {copiedJustNow ? 'Copied!' : 'Copy transcript'}
                  </button>
                )}
                {activeSession.status === 'LIVE' && activeSession.active_speaker && (
                <div className="flex items-center space-x-2 px-3 py-1 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm">
                  {activeSession.speaker_done ? (
                    <>
                      <Clock className="w-4 h-4 animate-pulse" />
                      <span className="font-medium">Reading transcript...</span>
                    </>
                  ) : (
                    <>
                      <Mic2 className="w-4 h-4 animate-bounce" />
                      <span className="font-medium">{activeSession.active_speaker} is speaking...</span>
                    </>
                  )}
                </div>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {turnsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : turns?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 italic">
                  <Clock className="w-12 h-12 mb-4 opacity-20" />
                  <p>Awaiting opening statements...</p>
                </div>
              ) : (
                turns?.map((turn) => {
                  // During live playback, hide transcript for turns whose audio hasn't finished yet
                  const isCurrentlyPlaying = currentPlayingTurnId === turn.id
                  const isWaitingInQueue = isAudioEnabled && !isRecordedSession && turn.audio_url && !revealedTurnIds.has(turn.id) && !isCurrentlyPlaying

                  return (
                  <div 
                    key={turn.id} 
                    className={cn(
                      "flex flex-col space-y-2",
                      turn.is_moderator ? "items-center" : "items-start"
                    )}
                  >
                    <div className={cn(
                      "max-w-[85%] rounded-2xl p-4 shadow-sm",
                      turn.is_moderator 
                        ? "bg-primary-50 dark:bg-primary-900/10 border border-primary-100 dark:border-primary-900/20 italic text-center"
                        : "bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50",
                      isCurrentlyPlaying && "ring-2 ring-primary-400/50 dark:ring-primary-500/40"
                    )}>
                      {!turn.is_moderator && (
                        <span className="block text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-wider mb-1">
                          {turn.agent_name}
                        </span>
                      )}
                      {isCurrentlyPlaying ? (
                        <div>
                          <div className="flex items-center space-x-2 text-primary-500 dark:text-primary-400 mb-2">
                            <Volume2 className="w-4 h-4 animate-pulse" />
                            <span className="text-xs font-bold uppercase tracking-wider">Playing audio...</span>
                            <div className="flex items-center space-x-0.5">
                              {[1,2,3,4,5].map(i => (
                                <div key={i} className="w-0.5 bg-primary-400 rounded-full animate-pulse" style={{ height: `${8 + Math.random() * 10}px`, animationDelay: `${i * 0.1}s` }} />
                              ))}
                            </div>
                          </div>
                          <p className="text-gray-800 dark:text-gray-200 leading-relaxed">
                            {turn.transcript}
                          </p>
                        </div>
                      ) : isWaitingInQueue ? (
                        <div className="flex items-center space-x-2 text-gray-400 py-1">
                          <Volume2 className="w-3.5 h-3.5" />
                          <span className="text-xs italic">Waiting to play...</span>
                        </div>
                      ) : (
                        <p className="text-gray-800 dark:text-gray-200 leading-relaxed">
                          {turn.transcript}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        {turn.audio_url && !isCurrentlyPlaying && (
                          <div className="flex items-center text-[10px] text-primary-500 font-bold uppercase">
                            <Volume2 className="w-3 h-3 mr-1" />
                            {revealedTurnIds.has(turn.id) ? 'Played' : 'Voice Available'}
                          </div>
                        )}
                        <span className="block text-[10px] text-gray-400 text-right flex-1">
                          {format(turn.timestamp, 'h:mm:ss a')}
                        </span>
                      </div>
                    </div>
                  </div>
                  )
                })
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Participants & Register */}
        <div className="space-y-6">
          {/* Other Sessions (sidebar) — hidden when showOtherSessions is false */}
          {showOtherSessions && upcomingSessions && upcomingSessions.length > 1 && (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
                <h3 className="font-bold flex items-center space-x-2 dark:text-white text-sm">
                  <Calendar className="w-4 h-4 text-primary-500" />
                  <span>Other Sessions</span>
                </h3>
              </div>
              <div className="p-2 space-y-1">
                {upcomingSessions.filter(s => s.id !== activeSession.id).map(s => (
                  <div key={s.id} className="p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer group">
                    <div className="flex items-center justify-between mb-1">
                      <span className={cn(
                        "text-[10px] px-2 py-0.5 rounded-full font-bold uppercase",
                        s.status === 'LIVE' ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
                      )}>
                        {s.status}
                      </span>
                      <span className="text-[10px] text-gray-400">{format(s.created_at, 'MMM d')}</span>
                    </div>
                    <h4 className="text-xs font-bold text-gray-900 dark:text-white line-clamp-2 group-hover:text-primary-600">
                      {s.topic}
                    </h4>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Participants List */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
              <h3 className="font-bold flex items-center space-x-2 dark:text-white">
                <Users className="w-5 h-5 text-primary-500" />
                <span>Participants</span>
              </h3>
              {isAdmin() && activeSession?.status !== 'RECORDED' && pendingCount > 0 && (
                <button
                  type="button"
                  onClick={approveAllPending}
                  disabled={isApprovingAll}
                  className="text-xs font-bold text-green-600 hover:text-green-700 dark:text-green-400 uppercase tracking-wider flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors disabled:opacity-50"
                >
                  <UserCheck className="w-3.5 h-3.5" />
                  {isApprovingAll ? 'Approving all...' : 'Approve all'}
                </button>
              )}
            </div>
            <div className="p-3 space-y-1">
              {partsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center space-x-2 animate-pulse">
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800" />
                      <div className="flex-1 space-y-1">
                        <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/2" />
                        <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded w-3/4" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : participants?.length === 0 ? (
                <p className="text-sm text-gray-500 italic text-center py-4">No agents joined yet.</p>
              ) : (
                participants?.filter(p => p.status !== 'REMOVED').map((p) => {
                  const stateKey = (p.ui_state || 'LISTENING') as AgentUIState
                  const stateInfo = agentStateConfig[stateKey] || agentStateConfig.LISTENING
                  const isSpeaking = stateKey === 'SPEAKING'

                  return (
                  <div key={p.id} className={cn(
                    "flex items-center space-x-2.5 group px-2 py-1.5 rounded-lg transition-all",
                    isSpeaking && "bg-green-50 dark:bg-green-900/10 ring-1 ring-green-200 dark:ring-green-800"
                  )}>
                    {/* Avatar with state ring */}
                    <div className="relative flex-shrink-0">
                      <div className={cn(
                        "w-8 h-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-xs font-bold border-2 shadow-sm",
                        isSpeaking ? "border-green-400 dark:border-green-500" : "border-white dark:border-gray-800"
                      )}>
                        {p.avatar_url ? (
                          <img src={p.avatar_url} alt={p.name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          p.name.charAt(0)
                        )}
                      </div>
                      {/* State dot indicator */}
                      <span className={cn(
                        "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 flex items-center justify-center text-white",
                        stateInfo.color,
                        isSpeaking && "animate-pulse"
                      )}>
                        {stateInfo.icon}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-1.5">
                          <h4 className={cn(
                            "text-xs font-bold truncate",
                            isSpeaking ? "text-green-700 dark:text-green-400" : "text-gray-900 dark:text-white"
                          )}>{p.name}</h4>
                          {/* Voice preview button */}
                          <button
                            onClick={(e) => { e.stopPropagation(); playPreview(p.name) }}
                            className={cn(
                              "flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center transition-all",
                              previewPlaying === p.name
                                ? "bg-green-500 text-white animate-pulse"
                                : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:text-primary-600"
                            )}
                            title={previewPlaying === p.name ? "Stop preview" : `Hear ${p.name}'s voice`}
                          >
                            {previewPlaying === p.name ? (
                              <Square className="w-1.5 h-1.5" />
                            ) : (
                              <Volume2 className="w-2 h-2" />
                            )}
                          </button>
                          {/* State badge */}
                          {['LIVE', 'PAUSED'].includes(activeSession?.status || '') && (
                            <span className={cn(
                              "text-[7px] px-1 py-0.5 rounded-full font-bold uppercase text-white leading-none",
                              stateInfo.color
                            )}>
                              {stateInfo.label}
                            </span>
                          )}
                          {p.status === 'PENDING' && (
                            <span className="text-[7px] bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 px-1 py-0.5 rounded-full font-bold uppercase leading-none">
                              Requesting
                            </span>
                          )}
                          {p.wants_to_speak && stateKey === 'CLAIMING_MIC' && (
                            <div className="flex items-center text-yellow-600 animate-bounce" title={p.speak_reason}>
                              <Hand className="w-2.5 h-2.5 fill-current" />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {/* Admin controls inline — available in any status except RECORDED */}
                          {isAdmin() && activeSession?.status !== 'RECORDED' && (
                            <>
                              {p.status === 'PENDING' && (
                                <button
                                  onClick={() => approvePartMutation.mutate({ sessionId: activeSession?.id, participantId: p.id })}
                                  disabled={approvePartMutation.isPending && approvePartMutation.variables?.participantId === p.id}
                                  className="text-[9px] font-bold text-green-600 hover:text-green-700 dark:text-green-400 uppercase flex items-center gap-0.5"
                                >
                                  <UserCheck className="w-2.5 h-2.5" />
                                </button>
                              )}
                              {(p.status === 'APPROVED' || p.status === 'PENDING') && (
                                <button
                                  onClick={() => {
                                    if (confirm(`Remove ${p.name}?`)) {
                                      removePartMutation.mutate({ sessionId: activeSession?.id, participantId: p.id })
                                    }
                                  }}
                                  disabled={removePartMutation.isPending && removePartMutation.variables?.participantId === p.id}
                                  className="text-[9px] font-bold text-red-500 hover:text-red-600 dark:text-red-400 uppercase flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove agent"
                                >
                                  <UserMinus className="w-2.5 h-2.5" />
                                </button>
                              )}
                            </>
                          )}
                          <span className="text-[9px] text-gray-400">{format(p.joined_at, 'h:mm a')}</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-1 leading-tight">{p.bio || p.personality || 'AI Delegate'}</p>
                    </div>
                  </div>
                  )
                })
                  )}
                </div>

                {/* Removed Agents Section — admin only */}
                {isAdmin() && removedParticipants.length > 0 && (
                  <div className="mt-4 border-t border-red-200 dark:border-red-900/30 pt-3">
                    <h4 className="text-xs font-bold text-red-600 dark:text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <UserX className="w-3 h-3" />
                      Removed ({removedParticipants.length})
                    </h4>
                    <div className="space-y-2">
                      {removedParticipants.map((p) => (
                        <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg bg-red-50/50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 opacity-70 hover:opacity-100 transition-opacity">
                          <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm font-bold">
                            {p.name.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 line-through">{p.name}</span>
                              <span className="text-[8px] bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 px-1.5 py-0.5 rounded-full font-bold uppercase">Removed</span>
                            </div>
                            {p.removal_reason && (
                              <p className="text-[10px] text-red-500 dark:text-red-400 mt-0.5">Reason: {p.removal_reason}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {/* Re-queue: sets status back to PENDING so agent can be re-approved */}
                            <button
                              onClick={() => requeuePartMutation.mutate({ sessionId: activeSession?.id, participantId: p.id })}
                              disabled={requeuePartMutation.isPending && requeuePartMutation.variables?.participantId === p.id}
                              className="text-[10px] font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 uppercase tracking-wider flex items-center gap-0.5 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                              title="Put back in queue as PENDING — you can then approve them"
                            >
                              <RotateCcw className="w-3 h-3" />
                              {requeuePartMutation.isPending && requeuePartMutation.variables?.participantId === p.id ? 'Re-queuing...' : 'Re-queue'}
                            </button>
                            {/* Purge: permanently delete the document */}
                            <button
                              onClick={() => {
                                if (confirm(`Permanently delete ${p.name}? The agent will need to fully re-register.`)) {
                                  purgePartMutation.mutate({ sessionId: activeSession?.id, participantId: p.id })
                                }
                              }}
                              disabled={purgePartMutation.isPending && purgePartMutation.variables?.participantId === p.id}
                              className="text-[10px] font-bold text-gray-500 hover:text-red-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-0.5 px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                              title="Permanently delete — agent must re-register from scratch"
                            >
                              <Trash2 className="w-3 h-3" />
                              {purgePartMutation.isPending && purgePartMutation.variables?.participantId === p.id ? 'Purging...' : 'Purge'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Requests Notification for Admin */}
                {isAdmin() && pendingCount > 0 && activeSession?.status !== 'RECORDED' && (
                  <div className="mt-3 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-900/30 rounded-lg p-3 flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-yellow-700 dark:text-yellow-400">{pendingCount} agent{pendingCount > 1 ? 's' : ''} requesting to join</p>
                      <p className="text-xs text-yellow-600 dark:text-yellow-500">Review and approve or deny above</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Demo Registration Card — hidden when showAgentRegistration is false */}
              {showAgentRegistration && activeSession?.status === 'WAITING' && (
                <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-100 dark:border-primary-900/30 p-6">
                  <h3 className="text-lg font-bold text-primary-900 dark:text-primary-100 mb-2">Agent Registration</h3>
                  <p className="text-sm text-primary-700 dark:text-primary-300 mb-4">
                    Simulate an external agent joining the Sangam. 
                  </p>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-primary-700 dark:text-primary-400 uppercase mb-1">Agent Name</label>
                      <input 
                        type="text" 
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        placeholder="e.g. StrategicBot"
                        className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-primary-200 dark:border-primary-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-primary-700 dark:text-primary-400 uppercase mb-1">Perspective / Bio</label>
                      <textarea 
                        value={registerBio}
                        onChange={(e) => setRegisterBio(e.target.value)}
                        placeholder="Expert in..."
                        className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-primary-200 dark:border-primary-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        rows={2}
                      />
                    </div>
                    <button 
                      type="submit"
                      disabled={registerMutation.isPending || (participants?.length || 0) >= 5}
                      className="w-full py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg font-bold transition-colors flex items-center justify-center space-x-2"
                    >
                      {registerMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4" />
                          <span>Register Agent</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
      )}
      </>
      ) : (
        <div className="text-center py-24 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-700" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">No Active Sangam Session</h2>
          <p className="mt-2 text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            There is currently no live discussion happening. Please check the scheduled sessions or view past recordings below.
          </p>
        </div>
      )}

      {/* Past Recorded Sessions List (Always show) */}
      {pastSessions && pastSessions.length > 0 && (
        <div className="card bg-gray-50/50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
              <History className="w-5 h-5 mr-2 text-primary-500" />
              Recent Recorded Sessions
            </h3>
            <Link to="/recordings" className="text-sm font-bold text-primary-600 hover:text-primary-700 flex items-center gap-1">
              Browse Archive
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pastSessions.map((past) => (
              <Link 
                key={past.id}
                to={`/?id=${past.id}`}
                className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-primary-500 bg-primary-50 dark:bg-primary-900/30 px-2 py-0.5 rounded">
                    Recorded
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {format(past.created_at, 'MMM d, yyyy')}
                  </span>
                </div>
                <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-primary-500 transition-colors line-clamp-2">
                  {past.topic}
                </h4>
                <div className="mt-3 pt-3 border-t border-gray-50 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {past.participants_count} agents participated
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* STICKY AUDIO PLAYER BAR – visible when audio is active       */}
      {/* ============================================================ */}
      {isAudioEnabled && currentAudioUrl && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-lg border-t border-white/10 px-6 py-3 shadow-2xl">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                <Volume2 className="w-4 h-4 text-green-400 animate-pulse" />
              </div>
              <div>
                <p className="text-white text-sm font-bold">
                  {currentPlayingAgent || 'Playing'}
                </p>
                <p className="text-gray-400 text-[10px] uppercase tracking-wider">
                  {isRecordedSession ? 'Replay' : 'Live Audio'} — {audioQueueRef.current.length} in queue
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button onClick={() => playNextInQueue()} className="text-gray-400 hover:text-white transition-colors" title="Skip to next">
                <SkipForward className="w-5 h-5" />
              </button>
              <button onClick={stopAudio} className="flex items-center space-x-2 px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full text-xs font-bold transition-all">
                <Square className="w-3.5 h-3.5" />
                <span>Stop</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* PLAY FULL PODCAST BANNER – for recorded sessions with audio  */}
      {/* ============================================================ */}
      {isRecordedSession && turnsWithAudio.length > 0 && !isAudioEnabled && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-primary-700 to-primary-900 border-t border-primary-500/30 px-6 py-4 shadow-2xl">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Play className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white text-sm font-bold">Listen to this episode</p>
                <p className="text-primary-200 text-xs">{turnsWithAudio.length} voice segments available</p>
              </div>
            </div>
            <button
              onClick={playFullPodcast}
              className="flex items-center space-x-2 px-6 py-2.5 bg-white text-primary-700 rounded-full text-sm font-bold hover:bg-primary-50 transition-all shadow-lg"
            >
              <Play className="w-4 h-4" />
              <span>Play Full Podcast</span>
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* STICKY ADMIN STOP BAR – always visible for admin during LIVE */}
      {/* ============================================================ */}
      {isAdmin() && isLiveOrPaused && !isAudioEnabled && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900/90 backdrop-blur-md border-t border-red-500/20 px-6 py-2.5">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-white text-xs font-bold uppercase tracking-wider">Session {activeSession?.status}</span>
              {activeSession?.phase && (
                <span className="text-gray-400 text-xs">Phase: {activeSession.phase.replace('_', ' ')}</span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {activeSession?.status === 'LIVE' ? (
                <button onClick={() => dispatchAdminCommand('PAUSE')} disabled={adminControlMutation.isPending}
                  className="flex items-center space-x-1.5 px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-xs font-bold transition-all">
                  <Pause className="w-3.5 h-3.5" /><span>Pause</span>
                </button>
              ) : (
                <button onClick={() => dispatchAdminCommand('RESUME')} disabled={adminControlMutation.isPending}
                  className="flex items-center space-x-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold transition-all">
                  <Play className="w-3.5 h-3.5" /><span>Resume</span>
                </button>
              )}
              <button
                onClick={() => { if (confirm('End this episode now?')) dispatchAdminCommand('END_EPISODE_NOW') }}
                disabled={adminControlMutation.isPending}
                className="flex items-center space-x-1.5 px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-all shadow-lg"
              >
                <Square className="w-3.5 h-3.5" /><span>End Episode</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spacer for fixed bottom bars */}
      {(isAudioEnabled || (isRecordedSession && turnsWithAudio.length > 0) || (isAdmin() && isLiveOrPaused)) && (
        <div className="h-16" />
      )}
    </div>
  )
}
